import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import { Maximize2, MapPin, Minimize2, Minus, Plus, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SETTINGS,
  GuidesOverlay,
  SettingsMenu,
  type SettingsState,
} from "./settings-menu";
import { AnnotationCanvas } from "./annotation-canvas";
import { SavedAnnotationsOverlay } from "./saved-annotations-overlay";
import { AnchorPinOverlay } from "./anchor-pin-overlay";
import { AnchorPinsDisplay } from "./anchor-pins-display";
import { useAnnotation } from "./annotation-context";
import type { VideoPlayerHandle } from "./video-player";
import type { WatchComment } from "@/hooks/use-watch";

/**
 * Image viewer (2026-07-18) — Frame.io-style still-image watch surface.
 *
 * Reuses the annotation + anchor-pin + guides / settings machinery from the
 * VideoPlayer so image comments carry the same spatial-anchor + annotation
 * shapes contract. Everything time-related (timeline, playback controls,
 * timecode display, playback speed, volume) is removed — a still image has
 * no time axis.
 *
 * Bottom bar layout (matches user's reference screenshot):
 *   LEFT   → nothing (kept balanced with the right cluster width)
 *   CENTER → zoom out · zoom % · zoom in
 *   RIGHT  → anchor pin · settings gear · fullscreen
 *
 * Exposes the same imperative handle shape as VideoPlayer so WatchShell can
 * hand a single `playerRef` to whichever surface is mounted. Time-related
 * methods are no-ops (seek does nothing, currentTime is always 0, isPlaying
 * always false, pause is a no-op).
 */

type Props = {
  src: string | null | undefined;
  posterLabel?: string;
  comments?: WatchComment[];
  mode?: "workspace" | "guest" | "share";
  watchKey?: string;
  currentVersionId?: string | null;
};

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 1.25; // one click = 25% up/down (multiplicative — matches keyboard shortcut)

export const ImageViewer = forwardRef(function ImageViewer(
  {
    src,
    posterLabel = "No image source",
    comments = [],
    mode = "workspace",
    watchKey,
    currentVersionId = null,
  }: Props,
  ref: Ref<VideoPlayerHandle>,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Image mode always starts in Fit; user zooms up/down from there. Reuse the
  // SettingsState shape so the SettingsMenu component works unchanged.
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);
  const annotation = useAnnotation();

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    function onChange() {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!rootRef.current) return;
    if (document.fullscreenElement === rootRef.current) {
      document.exitFullscreen().catch(() => {});
    } else {
      rootRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  // Handle shape parity with VideoPlayer — image has no time axis so every
  // time-related method degrades to a no-op / zero. This lets WatchShell hand
  // the same playerRef to any of the three surfaces without branching.
  useImperativeHandle(
    ref,
    () => ({
      seek: () => {},
      getCurrentTime: () => 0,
      isPlaying: () => false,
      pause: () => {},
    }),
    [],
  );

  // Keyboard shortcuts — same T/Y/+/-/Ctrl+0 set as the video player so muscle
  // memory carries over. Skip typing contexts.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        setSettings((s) => ({ ...s, zoomMode: "custom", zoomScale: 1 }));
        return;
      }
      switch (e.key.toLowerCase()) {
        case "t":
          setSettings((s) => ({ ...s, zoomMode: "fit", zoomScale: 1 }));
          break;
        case "y":
          setSettings((s) => ({ ...s, zoomMode: "fill", zoomScale: 1 }));
          break;
        case "+":
        case "=":
          setSettings((s) => ({
            ...s,
            zoomMode: "custom",
            zoomScale: Math.min(ZOOM_MAX, s.zoomScale * ZOOM_STEP),
          }));
          break;
        case "-":
          setSettings((s) => ({
            ...s,
            zoomMode: "custom",
            zoomScale: Math.max(ZOOM_MIN, s.zoomScale / ZOOM_STEP),
          }));
          break;
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Zoom buttons on the bottom bar. Same math as the keyboard shortcut branch
  // above; keep in sync if either changes.
  const zoomOut = useCallback(() => {
    setSettings((s) => ({
      ...s,
      zoomMode: "custom",
      zoomScale: Math.max(ZOOM_MIN, s.zoomScale / ZOOM_STEP),
    }));
  }, []);
  const zoomIn = useCallback(() => {
    setSettings((s) => ({
      ...s,
      zoomMode: "custom",
      zoomScale: Math.min(ZOOM_MAX, s.zoomScale * ZOOM_STEP),
    }));
  }, []);
  const zoomReset = useCallback(() => {
    setSettings((s) => ({ ...s, zoomMode: "custom", zoomScale: 1 }));
  }, []);

  const imgStyle = useMemo(() => {
    const baseFit: React.CSSProperties = { maxHeight: "100%", maxWidth: "100%" };
    if (settings.zoomMode === "fill") {
      return { objectFit: "cover" as const, width: "100%", height: "100%" };
    }
    if (settings.zoomMode === "custom") {
      return {
        ...baseFit,
        transform: `scale(${settings.zoomScale})`,
        transformOrigin: "center",
      };
    }
    return baseFit;
  }, [settings.zoomMode, settings.zoomScale]);

  // Human-readable zoom label for the bottom bar. Fit/Fill collapse the
  // percent readout so we don't lie about a scale that isn't literally applied.
  const zoomLabel = useMemo(() => {
    if (settings.zoomMode === "fit") return "Fit";
    if (settings.zoomMode === "fill") return "Fill";
    return `${Math.round(settings.zoomScale * 100)}%`;
  }, [settings.zoomMode, settings.zoomScale]);

  return (
    <div
      ref={rootRef}
      className="relative flex min-h-0 w-full flex-1 flex-col bg-black"
    >
      {/* Image canvas */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {src ? (
          <img src={src} alt="" style={imgStyle} draggable={false} />
        ) : (
          <div className="text-sm text-white/40">{posterLabel}</div>
        )}
        <GuidesOverlay ratio={settings.guidesRatio} showMask={settings.showMask} />
        {/* Live draw surface. Same rules as VideoPlayer — stays visible while
            draft shapes exist even after the toolbar closes. */}
        <AnnotationCanvas visible={annotation.isActive || annotation.shapes.length > 0} />
        {/* Saved-annotation overlay. On video/audio it filters by timecode
            proximity; images have no timecode so every annotation on this
            asset is "current" — the overlay component handles the null case
            (no timestampSeconds → no proximity filter). */}
        <SavedAnnotationsOverlay
          visible={settings.viewAnnotations && !annotation.isActive}
          comments={comments}
          currentTime={0}
        />
        {/* Anchor-pin drop — same overlay as video, but assetType="image"
            makes the POST body strip timestampSeconds. */}
        <AnchorPinOverlay
          mode={mode}
          watchKey={watchKey}
          currentVersionId={currentVersionId}
          getCurrentTime={() => 0}
          assetType="image"
        />
        {/* Persistent anchor pins for existing image comments. */}
        <AnchorPinsDisplay
          visible={settings.viewAnchoredComments && !annotation.anchorMode}
          comments={comments}
        />
      </div>

      {/* Bottom bar — image variant. No timeline scrubber, no playback
          controls, no timecode. Zoom out / % / zoom in in the center.
          Theme-aware 2026-07-23 to match Frame.io player-background-control. */}
      <div className="shrink-0 border-t border-black/5 bg-white text-foreground dark:border-white/5 dark:bg-black dark:text-white">
        <div className="flex items-center gap-2 px-4 py-2">
          {/* LEFT cluster — intentionally empty. Keeps the center cluster
              actually centered by matching the right cluster's width via
              flex-1 on the center wrapper. */}
          <div className="flex w-24 items-center gap-1" />

          {/* CENTER cluster — zoom out · % · zoom in. */}
          <div className="flex flex-1 items-center justify-center gap-1">
            <IconButton
              onClick={zoomOut}
              label="Zoom out"
              disabled={settings.zoomMode === "custom" && settings.zoomScale <= ZOOM_MIN + 1e-6}
            >
              <Minus className="h-4 w-4" />
            </IconButton>
            <button
              type="button"
              onClick={zoomReset}
              title="Reset zoom to 100%"
              aria-label="Reset zoom"
              className="min-w-12 rounded-md px-2 py-1 text-xs font-medium tabular-nums text-black/70 transition-colors hover:bg-black/10 hover:text-black dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
            >
              {zoomLabel}
            </button>
            <IconButton
              onClick={zoomIn}
              label="Zoom in"
              disabled={settings.zoomMode === "custom" && settings.zoomScale >= ZOOM_MAX - 1e-6}
            >
              <Plus className="h-4 w-4" />
            </IconButton>
          </div>

          {/* RIGHT cluster — anchor pin / settings / fullscreen. */}
          <div className="flex w-24 items-center justify-end gap-1">
            <IconButton
              onClick={() => {
                if (annotation.anchorMode) annotation.deactivateAnchor();
                else annotation.activateAnchor();
              }}
              label="Drop anchor pin"
              active={annotation.anchorMode}
            >
              <MapPin className="h-4 w-4" />
            </IconButton>
            <SettingsMenu value={settings} onChange={setSettings}>
              <Settings className="h-4 w-4" />
            </SettingsMenu>
            <IconButton
              onClick={toggleFullscreen}
              label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
});

function IconButton({
  children,
  onClick,
  label,
  disabled,
  active,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  label: string;
  disabled?: boolean;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-md text-black/70 transition-colors dark:text-white/80",
        !disabled && "hover:bg-black/10 hover:text-black dark:hover:bg-white/10 dark:hover:text-white",
        disabled && "cursor-not-allowed opacity-40",
        active && "text-primary dark:text-primary",
        className,
      )}
    >
      {children}
    </button>
  );
}
